import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../../components/Settings/SettingsPageContainer"
import {useSelector} from "react-redux"
import {useRPC} from "../../../../../hooks/rpc"
import {useLocalization} from "../../../../../hooks/localization"
import {ColumnModel, SettingsList} from "../../../../../components/Settings/SettingsList"
import {useSetSelector} from "../../../../../hooks/set-selector"
import {InputModel} from "../../../../../types/control"
import {UpDown} from "../components/UpDown"
import {useQuestion} from "../../../../../hooks/question"
import {useDispatch} from 'react-redux';
import {changeBusyAction} from '../../../../../store/ui/actions';

const keyExtractor = (item: InputModel) => item.id

export const SensorSortingScreen = memo(function SensorSortingScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const question = useQuestion()
    const dispatch = useDispatch();
    const inputList = useSelector(state => state.control.inputList)
    const inputSet = useSetSelector(state => state.control.inputList)
    const sensorTypeSet = useSetSelector(state => state.control.sensorTypeList)
    const sortedInputList = useMemo(
        () =>
            inputList
                .filter(items => items?.type_id === '4')
                .sort((a, b) => a?.sensor_settings?.["order"] - b?.sensor_settings?.["order"]),
        [inputList]
    )

    const [selectedItem, setSelectedItem] = useState<InputModel>(null)

    const swapItemOrders = useCallback(
        (itemA: InputModel, itemB: InputModel) => {
            dispatch(changeBusyAction(true))
            Promise.all([
                rpc.updateChangeOrder({
                    relational_id: itemA.id,
                    is_auto_sorting: false,
                    type: "sensor",
                    order: itemB?.sensor_settings?.["order"]
                }),
                rpc.updateChangeOrder({
                    relational_id: itemB.id,
                    is_auto_sorting: false,
                    type: "sensor",
                    order: itemA?.sensor_settings?.["order"]
                })
            ]).then(() => {
              setSelectedItem(null);
              dispatch(changeBusyAction(false));
            });
        },
        [rpc]
    )

    const onUpClick = useCallback(
        (index: number) => {
            swapItemOrders(sortedInputList[index - 1], sortedInputList[index])
        },
        [sortedInputList, swapItemOrders]
    )

    const onDownClick = useCallback(
        (index: number) => {
            swapItemOrders(sortedInputList[index + 1], sortedInputList[index])
        },
        [sortedInputList, swapItemOrders]
    )

    const displayNameExtractor = useCallback(
        (item: InputModel, column: ColumnModel, index?: number) => {
            if (column.key === "name") {
                return inputSet[item.id]?.name || "-"
            } else if (column.key === "sensor_type") {
                return sensorTypeSet[item?.sensor_settings?.sensor_type_id]?.name || "-"
            } else if (column.key === "order") {
                return (
                    <UpDown
                        index={index}
                        onUpClick={index === 0 ? undefined : onUpClick}
                        onDownClick={index === sortedInputList.length - 1 ? undefined : onDownClick}
                    />
                )
            }
            return item[column.key] || "-"
        },
        [onUpClick, onDownClick, inputSet, sortedInputList, sensorTypeSet]
    )

    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 3,
                name: t("name"),
                key: "name"
            },
            {
                length: 2,
                name: t("sensor_type"),
                key: "sensor_type"
            },
            {
                length: 2,
                name: t("order"),
                key: "order"
            }
        ],
        [t]
    )
    const onClickAutoSorting = useCallback(() => {
        return question(t("are_you_sure"), () => {
            return rpc.updateChangeOrder({
                is_auto_sorting: true,
                type: "sensor"
            })
        })
    }, [t, question, rpc])

    const buttons = useMemo(
        () => [
            {
                text: "A-Z (Auto)",
                className: "blue",
                onClick: onClickAutoSorting
            }
        ],
        [onClickAutoSorting]
    )

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings/custom-menu/"}>
                <SettingsList
                    sortDisabled={true}
                    columns={columns}
                    items={sortedInputList}
                    keyExtractor={keyExtractor}
                    displayNameExtractor={displayNameExtractor}
                    selectedItem={selectedItem}
                    onItemSelected={setSelectedItem}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
